import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { dataSelector } from '@nextiva/global-store';
import PropTypes from 'prop-types';
import { useQueryClient } from 'react-query';
import { userDeactivateBulk } from 'api/userApi';
import { NotifyModal } from 'components';
import { DIALOG_KINDS } from 'constants/dialogKinds';
import { useRootUserList } from 'customHooks/userCustomHooks';
import {
  DEACTIVATE_USERS_MESSAGE,
  DEACTIVATE_VOICE_USER_MESSAGE,
} from 'constants/manageUsers';
import { isAccountManager } from 'utils/validators';
import { FeatureContext } from 'components/AccessResolvers/featureContext';

import { getCXWorkspacesAtUserLevel } from 'services/CXWorkspaces/CXWorkspacesService';
import { UserContext, actionTypes } from '../usersContext';
import { useGetCompanyInfo, useSnackbar } from '../../../../hooks';

const namesMaxChars = 250;

const DeactivateUsersModal = ({ toggleModal }) => {
  const queryClient = useQueryClient();
  const [modalTitle, setModalTitle] = useState('');
  const [userNames, setUserNames] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalMessage, setModalMessage] = useState(DEACTIVATE_USERS_MESSAGE);
  const [managerName, setManagerName] = useState('');
  const setSnackbar = useSnackbar();
  const {
    featureState: { authV3Enabled, globalNextivaCXFlowEnabled },
  } = useContext(FeatureContext);

  const { updateRootUserList } = useRootUserList();

  const {
    userState: { selectedUserList },
    dispatch,
  } = useContext(UserContext);
  const companyInfo = useGetCompanyInfo();
  const [selectedUsers, setSelectedUsers] = useState([...selectedUserList]);

  const globalStoreData = useSelector(dataSelector);
  const sso = globalStoreData?.sso;
  const corpAccountNumber =
    globalStoreData?.user?.accountInfo?.corpAcctNbr ??
    globalStoreData?.user?.profileDTO?.corpAcctNbr;

  const removeManager = () => {
    const {
      account_manager: { contact: { first_name, last_name } = {} } = {},
      accountManager: { contact: { firstName, lastName } = {} } = {},
    } = companyInfo.data || {};

    // account manager is in list
    const index = selectedUsers.findIndex((user) => {
      return isAccountManager(user, companyInfo.data || {});
    });

    if (index !== -1) {
      selectedUsers.splice(index, 1);
      setSelectedUsers(selectedUsers);
      setManagerName(`${first_name ?? firstName} ${last_name ?? lastName}`);
    }
  };

  useEffect(() => {
    removeManager();

    // nextOsIdent equals voice user
    const nextOsIdent = selectedUsers.some(
      (user) =>
        !!user.profiles[0].nextOS.primary_identifier ||
        !!user.profiles[0].nextOS.primaryIdentifier,
    );

    if (nextOsIdent) setModalMessage(DEACTIVATE_VOICE_USER_MESSAGE);

    setModalTitle(`Deactivate ${selectedUsers.length} users`);
    const userNamesRaw = selectedUsers
      .map(
        (user) =>
          `${user.first_name || user.firstName} ${
            user.last_name || user.lastName
          }`,
      )
      .join(', ');

    if (userNamesRaw.length > namesMaxChars) {
      setUserNames(`${userNamesRaw.substr(0, namesMaxChars)}\u2026`);
    } else {
      setUserNames(userNamesRaw);
    }
  }, [toggleModal]);

  const onDeactivateUsers = useCallback(async () => {
    let isPrimaryAccountOwner = false;
    if (!isLoading) {
      setIsLoading(true);

      if (selectedUsers.length === 1 && globalNextivaCXFlowEnabled) {
        await getCXWorkspacesAtUserLevel(
          sso,
          corpAccountNumber,
          selectedUsers[0]?.nextivaUuid,
        )
          .then((response) => {
            isPrimaryAccountOwner = response?.data?.primaryAccountOwner;
          })
          .catch(() => {
            // TODO: add proper error message in follow up ticket
            // eslint-disable-next-line no-console
            console.error('Could not find CX user');
          });
      }

      const loggedInUser = selectedUsers.find(
        (user) => user.email === globalStoreData.user?.email,
      );

      if (isPrimaryAccountOwner) {
        setIsLoading(false);
        toggleModal();
        setSnackbar({
          message: 'You can not deactivate a CX Primary Account Owner',
          severity: 'error',
        });
      } else if (loggedInUser) {
        setIsLoading(false);
        toggleModal();
        setSnackbar({
          message: 'You cannot deactivate a logged-in user',
          severity: 'error',
        });
      } else {
        return userDeactivateBulk(
          authV3Enabled
            ? {
                bulkUsers: selectedUsers.map((user) => ({
                  nextivaUuid: user.nextivaUuid,
                })),
              }
            : selectedUsers.map((user) => ({
                login_id: user.email, // eslint-disable-line camelcase
                profile_id: user.profiles[0].id, // eslint-disable-line camelcase
              })),
          authV3Enabled,
        )
          .then((results) => {
            updateRootUserList();
            queryClient.invalidateQueries(['getUsersBasicInfo']);
            setSnackbar({
              message: 'Success. Users successfully deactivated.',
              severity: 'success',
            });
            const idsToDeactivate = Object.entries(results.successes)
              .filter((res) => res[1] === 'OK')
              .map((res) => parseInt(res[0], 10));

            if (idsToDeactivate) {
              dispatch({
                type: actionTypes.TOGGLE_USERS_ACTIVE_STATUS,
                profileIds: idsToDeactivate,
              });
            }
          })
          .catch(() => {
            setSnackbar({
              message: 'An error occurred during deactivation.',
              severity: 'error',
            });
          })
          .finally(() => {
            queryClient.invalidateQueries(['licenseList']);
            setIsLoading(false);
            toggleModal();
            dispatch({
              type: actionTypes.SET_TABLE_HEADER_STATE,
              tableHeaderState: false,
            });
          });
      }
    }

    return 'undefined';
  }, [toggleModal]);

  return (
    <NotifyModal
      title={modalTitle}
      confirmText="Deactivate"
      onCancel={toggleModal}
      onConfirm={onDeactivateUsers}
      mode={DIALOG_KINDS.ERROR}
      isLoading={isLoading}
      disabled={selectedUsers.length === 0}
      open
    >
      <div className="m-notify-modal">
        {managerName && (
          <div className="m-notify-modal__main m-notify-modal__main--bold">
            You cannot deactivate the account manager {managerName}, please
            contact support to remove this user. They have been removed from the
            list of users to be deactivated.
          </div>
        )}
        {selectedUsers.length > 0 && (
          <div className="m-notify-modal__main">{modalMessage}</div>
        )}
        <div className="m-notify-modal-modal__userList">{userNames}</div>
      </div>
    </NotifyModal>
  );
};

DeactivateUsersModal.propTypes = {
  toggleModal: PropTypes.func.isRequired,
};

export default DeactivateUsersModal;
